<template>
  <div class="dates-calendar">
    <div class="dc-header">
      <div class="dc-year">
        <el-button
          icon="el-icon-arrow-left"
          size="mini"
          circle
          @click="year--"
        ></el-button>
        <span class="dc-year__label">{{ year }}</span>
        <el-button
          icon="el-icon-arrow-right"
          size="mini"
          circle
          @click="year++"
        ></el-button>
      </div>
      <div class="dc-months">
        <button
          v-for="(name, m) in MONTHS"
          :key="m"
          type="button"
          class="dc-month"
          :class="{ 'is-active': m === month }"
          @click="month = m"
        >
          {{ name }}
          <span v-if="monthCounts[monthKey(year, m)]" class="dc-month__badge">
            {{ monthCounts[monthKey(year, m)] }}
          </span>
        </button>
      </div>
      <div class="dc-summary">
        <help-tip
          title="Выбор дат"
          content="Нажмите на дату, чтобы выбрать её, или зажмите и протяните для выбора интервала. Повторное нажатие снимает выбор."
        ></help-tip>
        <span class="dc-count">
          Выбрано дней: <b>{{ value.length }}</b>
        </span>
        <el-button
          type="text"
          class="dc-clear"
          :disabled="!value.length"
          @click="$emit('input', [])"
        >
          Очистить
        </el-button>
      </div>
    </div>

    <div class="dc-grid">
      <div
        v-for="(name, i) in WEEKDAYS"
        :key="name"
        class="dc-weekday"
        :class="{ 'is-weekend': i > 4 }"
      >
        {{ name }}
      </div>
      <div
        v-for="day in days"
        :key="day.key"
        class="dc-day"
        :class="{
          'is-other': day.other,
          'is-disabled': day.disabled,
          'is-selected': day.selected,
          'is-today': day.today,
          'is-weekend': day.weekend,
          'is-marked': day.mark,
        }"
        :title="day.mark"
        @mousedown.prevent="startDrag(day, $event)"
        @mouseenter="moveDrag(day)"
      >
        <span class="dc-day__number">{{ day.date }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import HelpTip from "@/components/ecologist/dataEntryNew/helpTip";

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export default {
  name: "datesCalendar",
  components: { HelpTip },
  model: { prop: "value", event: "input" },
  props: {
    // Выбранные даты в формате YYYY-MM-DD
    value: { type: Array, default: () => [] },
    // (key: YYYY-MM-DD) => boolean - дату нельзя выбрать
    isDisabled: { type: Function, default: () => false },
    // Даты с проблемой { [YYYY-MM-DD]: причина } - подсвечиваются красным, причина в подсказке
    marked: { type: Object, default: () => ({}) },
  },
  data() {
    const now = new Date();
    return {
      MONTHS: [
        "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
        "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
      ],
      WEEKDAYS: ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"],
      year: now.getFullYear(),
      month: now.getMonth(),
      todayKey: toKey(now),
      // { anchor, mode: "add" | "remove", base: Set } пока зажата кнопка мыши
      drag: null,
    };
  },

  computed: {
    selected() {
      return new Set(this.value);
    },
    monthCounts() {
      const counts = {};
      this.value.forEach((key) => {
        const month = key.slice(0, 7);
        counts[month] = (counts[month] || 0) + 1;
      });
      return counts;
    },
    days() {
      const first = new Date(this.year, this.month, 1);
      const offset = (first.getDay() + 6) % 7;
      // Всегда 6 недель, как в el-date-picker, чтобы высота не прыгала между месяцами
      const total = 42;

      const days = [];
      for (let i = 0; i < total; i++) {
        const date = new Date(this.year, this.month, 1 - offset + i);
        const key = toKey(date);
        const other = date.getMonth() !== this.month;
        days.push({
          key,
          date: date.getDate(),
          other,
          disabled: !other && this.isDisabled(key),
          selected: !other && this.selected.has(key),
          today: key === this.todayKey,
          mark: other ? null : this.marked[key] || null,
          weekend: i % 7 > 4,
        });
      }
      return days;
    },
  },

  methods: {
    // Перелистнуть календарь на месяц с датой YYYY-MM-DD
    showDate(key) {
      const date = fromKey(key);
      this.year = date.getFullYear();
      this.month = date.getMonth();
    },

    monthKey(year, month) {
      return `${year}-${pad(month + 1)}`;
    },

    startDrag(day, event) {
      if (event.button !== 0 || day.other || day.disabled) return;
      this.drag = {
        anchor: day.key,
        mode: day.selected ? "remove" : "add",
        base: new Set(this.value),
      };
      this.applyRange(day.key);
    },

    moveDrag(day) {
      if (this.drag && !day.other) this.applyRange(day.key);
    },

    endDrag() {
      this.drag = null;
    },

    // Применить действие ко всем датам от якоря до key поверх состояния на начало протягивания
    applyRange(key) {
      const { anchor, mode, base } = this.drag;
      const [from, to] = anchor < key ? [anchor, key] : [key, anchor];
      const result = new Set(base);
      for (
        let d = fromKey(from);
        toKey(d) <= to;
        d.setDate(d.getDate() + 1)
      ) {
        const k = toKey(d);
        if (this.isDisabled(k)) continue;
        if (mode === "add") result.add(k);
        else result.delete(k);
      }
      this.$emit("input", [...result].sort());
    },
  },

  mounted() {
    window.addEventListener("mouseup", this.endDrag);
  },

  beforeDestroy() {
    window.removeEventListener("mouseup", this.endDrag);
  },
};
</script>

<style scoped>
/* Шапка, месяцы и сетка - во всю ширину своей колонки */
.dates-calendar {
  display: flex;
  flex-direction: column;
  user-select: none;
  --primary: #409eff;
  --primary-light: #ecf5ff;
  --border: #ebeef5;
  --text: #303133;
  --muted: #c0c4cc;
  --weekend: #f56c6c;
}

/* Год и счётчик - по краям первой строки, месяцы - отдельной строкой во всю ширину */
.dc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 12px;
}
.dc-year {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dc-year__label {
  min-width: 44px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: var(--text);
}
/* Месяцы - всегда два ряда по полгода */
.dc-months {
  order: 1;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px 4px;
  width: 100%;
}
.dc-month {
  position: relative;
  padding: 5px 10px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #fff;
  color: #606266;
  /* У <button> браузер ставит системный шрифт - берём шрифт страницы */
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background-color 0.2s;
}
.dc-month:hover {
  color: var(--primary);
  border-color: #c6e2ff;
  background: var(--primary-light);
}
.dc-month.is-active {
  color: #fff;
  border-color: var(--primary);
  background: var(--primary);
}
.dc-month__badge {
  position: absolute;
  top: -7px;
  right: -5px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #67c23a;
  color: #fff;
  font-size: 11px;
  line-height: 16px;
}
.dc-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
}
/* Как заголовки групп карточек («Поступление», «Расход»), число - тёмным */
.dc-count {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.dc-clear {
  font-size: 13px;
  font-weight: 600;
}
.dc-count b {
  color: var(--text);
}

.dc-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: auto;
  gap: 4px;
}
.dc-weekday {
  padding: 4px 0 6px;
  border-bottom: 2px solid var(--border);
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.dc-weekday.is-weekend {
  color: var(--weekend);
  border-bottom-color: #fde2e2;
}

/* Высота дня равна ширине - дни квадратные */
.dc-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fafbfc;
  color: var(--text);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s,
    box-shadow 0.15s;
}
.dc-day.is-weekend {
  background: #fef6f6;
  border-color: #fde2e2;
  color: var(--weekend);
}
.dc-day:hover {
  background: var(--primary-light);
  border-color: #b3d8ff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.15);
}
.dc-day.is-today {
  border-color: var(--primary);
  box-shadow: inset 0 0 0 1px var(--primary);
}
.dc-day.is-today .dc-day__number {
  font-weight: bold;
}
.dc-day.is-selected {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.dc-day.is-selected:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}
.dc-day.is-marked {
  background: #fef0f0;
  border-color: #f56c6c;
  color: #f56c6c;
}
.dc-day.is-marked.is-selected {
  background: #f56c6c;
  color: #fff;
}
.dc-day.is-other {
  background: transparent;
  border-color: transparent;
  color: #dcdfe6;
  cursor: default;
  pointer-events: none;
  /*
    Ячейки с общими датами переиспользуются при смене месяца. Переход берётся из конечного
    стиля, поэтому в день соседнего месяца ячейка переключается мгновенно, без «мигания»
  */
  transition: none;
}
.dc-day.is-disabled {
  background: repeating-linear-gradient(
    -45deg,
    #f5f7fa,
    #f5f7fa 6px,
    #ebeef5 6px,
    #ebeef5 12px
  );
  border-color: var(--border);
  box-shadow: none;
  color: var(--muted);
  cursor: not-allowed;
}
</style>
